import { useTranslation } from '@mint-lib/i18n';
import {
  Download,
  Share,
  StatusChange,
  TrashCan,
  Upload,
} from '@mint-lib/icons';
import { Button, Divider, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type Props = {
  selectedCount: number;
  isFileSystemEnabled: boolean;
  canUploadKBFile: boolean;
  canDownloadKBFile: boolean;
  canDeleteSelection: boolean;
  hasSelectedFolders: boolean;
  onOpenUploadDialog: () => void;
  onDownloadSelection: () => void;
  onDeleteSelection: () => void;
  onOpenMoveDialog: () => void;
};

const FileSystemSelectionActions = ({
  canDownloadKBFile,
  canDeleteSelection,
  hasSelectedFolders,
  onDownloadSelection,
  onDeleteSelection,
  onOpenMoveDialog,
}: Pick<
  Props,
  | 'canDownloadKBFile'
  | 'canDeleteSelection'
  | 'hasSelectedFolders'
  | 'onDownloadSelection'
  | 'onDeleteSelection'
  | 'onOpenMoveDialog'
>) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <>
      <Button
        variant="outlined"
        data-dd-action-name="Knowledge base: share selection"
      >
        <Share />
        {t('Share')}
      </Button>

      <Button
        variant="outlined"
        data-dd-action-name="Knowledge base: move selection"
        onClick={onOpenMoveDialog}
      >
        <StatusChange />
        {t('Move to')}
      </Button>

      <Divider
        orientation="vertical"
        className="data-[orientation=vertical]:h-6"
      />

      <Button
        icon
        variant="text"
        aria-label={t('Download')}
        data-dd-action-name="Knowledge base: download selection"
        onClick={onDownloadSelection}
        disabled={!canDownloadKBFile || hasSelectedFolders}
      >
        <Download className="fill-text-primary" />
      </Button>

      <Button
        icon
        color="error"
        variant="text"
        aria-label={t('Delete')}
        data-dd-action-name="Knowledge base: delete selection"
        onClick={onDeleteSelection}
        disabled={!canDeleteSelection}
      >
        <TrashCan />
      </Button>
    </>
  );
};

const LegacySelectionActions = ({
  canDownloadKBFile,
  canDeleteSelection,
  hasSelectedFolders,
  onDownloadSelection,
  onDeleteSelection,
}: Pick<
  Props,
  | 'canDownloadKBFile'
  | 'canDeleteSelection'
  | 'hasSelectedFolders'
  | 'onDownloadSelection'
  | 'onDeleteSelection'
>) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        onClick={onDownloadSelection}
        disabled={!canDownloadKBFile || hasSelectedFolders}
      >
        <Download />
        {t('Download')}
      </Button>

      <Button
        color="error"
        variant="outlined"
        onClick={onDeleteSelection}
        disabled={!canDeleteSelection}
      >
        <TrashCan />
        {t('Delete')}
      </Button>
    </>
  );
};

/**
 * The leading slot of the Knowledge base toolbar: it carries the upload action
 * until rows are picked, then hands the space over to the selection actions.
 */
const ToolbarActions = ({
  selectedCount,
  isFileSystemEnabled,
  canUploadKBFile,
  canDownloadKBFile,
  canDeleteSelection,
  hasSelectedFolders,
  onOpenUploadDialog,
  onDownloadSelection,
  onDeleteSelection,
  onOpenMoveDialog,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  if (!selectedCount) {
    return (
      <Button
        className="flex flex-row gap-2"
        onClick={onOpenUploadDialog}
        disabled={!canUploadKBFile}
      >
        <Upload />
        {t('Upload file')}
      </Button>
    );
  }

  const SelectionActions = isFileSystemEnabled
    ? FileSystemSelectionActions
    : LegacySelectionActions;

  return (
    <div className="flex flex-row items-center gap-3">
      <p
        className={cn(
          typographyVariants({ variant: 'bodyShort01' }),
          'text-nowrap',
        )}
      >
        {t('{{selectedQty}} selected', { selectedQty: selectedCount })}
      </p>
      <Divider
        orientation="vertical"
        className="data-[orientation=vertical]:h-6"
      />

      <SelectionActions
        canDownloadKBFile={canDownloadKBFile}
        canDeleteSelection={canDeleteSelection}
        hasSelectedFolders={hasSelectedFolders}
        onDownloadSelection={onDownloadSelection}
        onDeleteSelection={onDeleteSelection}
        onOpenMoveDialog={onOpenMoveDialog}
      />
    </div>
  );
};

export { ToolbarActions };
